import { useEffect, useMemo, useState } from "react";
import {
  buildModularMealPrepEditorialDraft,
  parseModularMealPrepOverride,
  updateModularMealPrepPreferences,
} from "../services/mealPrep";
import Icon from "./Icon";

const DAYS = [
  ["monday", "Lundi"],
  ["tuesday", "Mardi"],
  ["wednesday", "Mercredi"],
  ["thursday", "Jeudi"],
  ["friday", "Vendredi"],
  ["saturday", "Samedi"],
  ["sunday", "Dimanche"],
];
const DAY_LABEL = Object.fromEntries(DAYS);
const CATEGORY_LABELS = {
  protein: "Protéines",
  vegetable: "Légumes",
  fruit: "Fruits",
  starch: "Féculents",
  fresh: "Produits frais",
  fat: "Matières grasses",
  grocery: "Épicerie",
  herb_spice: "Herbes et épices",
  other: "Autres",
};

const card = {
  border: "1px solid rgba(212, 201, 168, 0.16)",
  borderRadius: 12,
  background: "rgba(255,255,255,0.025)",
  overflow: "hidden",
};
const panel = {
  border: "1px solid rgba(212,201,168,.12)",
  borderRadius: 10,
  background: "rgba(0,0,0,.12)",
};
const control = {
  border: "1px solid rgba(212,201,168,.18)",
  borderRadius: 8,
  background: "rgba(0,0,0,.18)",
  color: "#f0f0e8",
  font: "inherit",
};
const button = {
  ...control,
  padding: "7px 11px",
  fontSize: ".74rem",
  cursor: "pointer",
};

function formatQuantity(quantity, unit) {
  if (quantity === null || quantity === undefined) return "Selon la recette";
  const rounded = Math.round(quantity * 10) / 10;
  return `${new Intl.NumberFormat("fr-CH", { maximumFractionDigits: 1 }).format(rounded)} ${unit}`;
}

function sameValue(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function groupedShoppingList(items) {
  const groups = new Map();
  for (const item of items) {
    const category = item.category || "other";
    if (!groups.has(category)) groups.set(category, []);
    groups.get(category).push(item);
  }
  return Array.from(groups.entries());
}

function formatMealDays(days) {
  if (!Array.isArray(days) || days.length === 0) return "Aucun repas rattaché";
  if (days.length === 1) return DAY_LABEL[days[0]];
  return `${DAY_LABEL[days[0]]} → ${DAY_LABEL[days[days.length - 1]]}`;
}

export function canUseModularMealPrep(weekMeals) {
  return buildModularMealPrepEditorialDraft(weekMeals).result.ready === true;
}

export default function ModularMealPrepEditorialCard({
  weekMeals,
  value,
  onSave,
  onDirtyChange,
  compactReview = false,
}) {
  const parsed = useMemo(() => parseModularMealPrepOverride(value), [value]);
  const initial = useMemo(
    () => buildModularMealPrepEditorialDraft(weekMeals, parsed || {}),
    [weekMeals, parsed],
  );
  const [draft, setDraft] = useState(initial.override);
  const [baseline, setBaseline] = useState(parsed);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(!compactReview);

  useEffect(() => {
    setDraft(initial.override);
    setBaseline(parsed);
    setMessage(null);
  }, [initial, parsed]);

  const preview = useMemo(
    () => buildModularMealPrepEditorialDraft(weekMeals, draft).result,
    [weekMeals, draft],
  );
  const dirty = !sameValue(draft, baseline);
  const readOnly = typeof onSave !== "function";

  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  function update(patch) {
    setDraft((current) => updateModularMealPrepPreferences(weekMeals, current, patch).override);
    setMessage(null);
  }

  function updateDay(index, nextDay) {
    const preferredDays = [...draft.preferred_days];
    const previousDay = preferredDays[index];
    const duplicateIndex = preferredDays.indexOf(nextDay);
    if (duplicateIndex >= 0 && duplicateIndex !== index) preferredDays[duplicateIndex] = previousDay;
    preferredDays[index] = nextDay;
    update({ preferred_days: preferredDays });
  }

  async function persist(next, successMessage) {
    const candidate = buildModularMealPrepEditorialDraft(weekMeals, next);
    const normalized = parseModularMealPrepOverride(candidate.override);
    if (!candidate.result.ready || !normalized) {
      setMessage({ kind: "error", text: candidate.result.issues?.[0] || "Meal Prep à vérifier." });
      return false;
    }
    if (typeof onSave !== "function") {
      setMessage({ kind: "error", text: "Enregistrement indisponible dans cette étape." });
      return false;
    }
    setSaving(true);
    setMessage(null);
    try {
      await onSave(normalized);
      setDraft(normalized);
      setBaseline(normalized);
      setMessage({ kind: "success", text: successMessage });
      return true;
    } catch (error) {
      setMessage({ kind: "error", text: error?.message || "Enregistrement impossible." });
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function saveDraft() {
    await persist({ ...draft, status: "draft" }, "Brouillon Meal Prep enregistré.");
  }

  async function validate() {
    if (dirty || !preview.ready) return;
    await persist({ ...draft, status: "validated" }, "Meal Prep validé par Anissa.");
  }

  if (value && !parsed) {
    return (
      <section style={card} aria-labelledby="modular-meal-prep-title">
        <div style={{ padding: 18 }}>
          <h4 id="modular-meal-prep-title" style={{ margin: 0, color: "#f0f0e8" }}>Meal Prep</h4>
          <p role="alert" style={{ margin: "8px 0 0", color: "#f5c6c6", fontSize: ".78rem" }}>
            Le brouillon v2 enregistré est à vérifier. Il ne sera pas utilisé tant qu’il n’est pas réinitialisé.
          </p>
          <button type="button" onClick={() => persist(initial.override, "Brouillon réinitialisé.")} disabled={readOnly} style={{ ...button, marginTop: 12, opacity: readOnly ? .45 : 1 }}>
            Réinitialiser le brouillon
          </button>
        </div>
      </section>
    );
  }

  const shoppingGroups = groupedShoppingList(preview.shoppingList || []);
  const validated = draft.status === "validated" && !dirty;
  const preparationCounts = preview.sessions.map((session) => session.components.length);
  const totalPreparations = preparationCounts.reduce((total, count) => total + count, 0);
  const uniformPreparationCount = preparationCounts.length > 0
    && preparationCounts.every((count) => count === preparationCounts[0])
    ? preparationCounts[0]
    : null;
  const preparationLabel = uniformPreparationCount !== null && preparationCounts.length > 1
    ? `${uniformPreparationCount} préparations / session`
    : `${totalPreparations} préparation${totalPreparations > 1 ? "s" : ""}`;
  const preferredDaysLabel = draft.preferred_days.map((day) => DAY_LABEL[day]).filter(Boolean).join(" / ");

  return (
    <section style={card} aria-labelledby="modular-meal-prep-title">
      <header style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "18px 18px 16px" }}>
        <Icon type="clipboard" size={18} style={{ color: "#d4c9a8", marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, color: "#8a8a7a", fontSize: ".66rem", fontWeight: 700, letterSpacing: ".13em", textTransform: "uppercase" }}>
            Organisation de la semaine
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 4, flexWrap: "wrap" }}>
            <h4 id="modular-meal-prep-title" style={{ margin: 0, color: "#f0f0e8", fontSize: "1rem" }}>Meal Prep</h4>
            <span style={{ borderRadius: 999, padding: "3px 8px", color: validated ? "#a8e0c0" : "#d4c9a8", background: validated ? "rgba(138,191,154,.10)" : "rgba(212,201,168,.08)", fontSize: ".66rem", fontWeight: 700 }}>
              {validated ? "Validé" : "Brouillon"}
            </span>
          </div>
          <p style={{ margin: "6px 0 0", color: "#99998d", fontSize: ".78rem", lineHeight: 1.55 }}>
            Ajuste l’organisation. Les quantités et les courses se recalculent immédiatement depuis les recettes choisies.
          </p>
        </div>
      </header>

      {compactReview && (
        <section className="delivery-quick-review" aria-label="Résumé du Meal Prep">
          <div className="delivery-quick-review__heading">
            <div>
              <p>État de la section</p>
              <h5>Meal Prep v2 — {validated ? "Validé" : "À vérifier"}</h5>
            </div>
            <span className={`delivery-quick-review__state${validated ? " is-ready" : ""}`}>
              {validated ? "Prêt" : "Brouillon"}
            </span>
          </div>
          <dl className="delivery-quick-review__metrics">
            <div><dt>{draft.people}</dt><dd>personne{draft.people > 1 ? "s" : ""}</dd></div>
            <div><dt>{draft.session_count}</dt><dd>session{draft.session_count > 1 ? "s" : ""}</dd></div>
            <div><dt>{preferredDaysLabel || "À choisir"}</dt><dd>jours de préparation</dd></div>
            <div><dt>{preparationLabel}</dt><dd>organisation calculée</dd></div>
            <div><dt>{preview.shoppingList.length}</dt><dd>articles de courses</dd></div>
          </dl>
          <button
            type="button"
            className="delivery-quick-review__toggle"
            aria-expanded={detailsOpen}
            onClick={() => setDetailsOpen((open) => !open)}
          >
            {detailsOpen ? "Masquer le détail" : "Voir le détail / Modifier"}
          </button>
        </section>
      )}

      <div hidden={compactReview && !detailsOpen} style={{ borderTop: "1px solid rgba(212,201,168,.10)", padding: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          <div style={{ ...panel, padding: 12 }}>
            <span style={{ display: "block", color: "#8a8a7a", fontSize: ".68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em" }}>Personnes</span>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 9 }}>
              <button type="button" aria-label="Retirer une personne" onClick={() => update({ people: Math.max(1, draft.people - 1) })} disabled={readOnly || draft.people <= 1} style={{ ...button, padding: 7 }}><Icon type="minus" size={14} /></button>
              <strong aria-live="polite" style={{ minWidth: 28, color: "#f0f0e8", textAlign: "center" }}>{draft.people}</strong>
              <button type="button" aria-label="Ajouter une personne" onClick={() => update({ people: Math.min(20, draft.people + 1) })} disabled={readOnly || draft.people >= 20} style={{ ...button, padding: 7 }}><Icon type="plus" size={14} /></button>
            </div>
          </div>

          <fieldset style={{ ...panel, padding: 12, margin: 0, minWidth: 0 }}>
            <legend style={{ padding: 0, color: "#8a8a7a", fontSize: ".68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em" }}>Sessions</legend>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginTop: 4 }}>
              {[1, 2, 3].map((count) => (
                <button
                  type="button"
                  key={count}
                  aria-pressed={draft.session_count === count}
                  onClick={() => update({ session_count: count })}
                  disabled={readOnly}
                  style={{ ...button, color: draft.session_count === count ? "#a8e0c0" : "#99998d", background: draft.session_count === count ? "rgba(138,191,154,.12)" : "rgba(0,0,0,.14)", borderColor: draft.session_count === count ? "rgba(138,191,154,.32)" : "rgba(212,201,168,.14)" }}
                >{count}</button>
              ))}
            </div>
          </fieldset>
        </div>

        <div style={{ ...panel, padding: 12, marginTop: 10 }}>
          <p style={{ margin: "0 0 9px", color: "#8a8a7a", fontSize: ".68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em" }}>Jours de préparation</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
            {draft.preferred_days.map((day, index) => (
              <label key={`session-day-${index + 1}`} style={{ color: "#cfcfc4", fontSize: ".76rem" }}>
                <span style={{ display: "block", marginBottom: 5 }}>Session {index + 1}</span>
                <select aria-label={`Jour de préparation de la session ${index + 1}`} value={day} onChange={(event) => updateDay(index, event.target.value)} disabled={readOnly} style={{ ...control, width: "100%", padding: "8px 10px" }}>
                  {DAYS.map(([valueDay, label]) => <option key={valueDay} value={valueDay}>{label}</option>)}
                </select>
              </label>
            ))}
          </div>
        </div>

        {!preview.ready ? (
          <p role="alert" style={{ margin: "12px 0 0", color: "#e5c878", fontSize: ".76rem" }}>
            {preview.issues?.[0] || "Les recettes doivent être structurées avant de préparer le Meal Prep v2."}
          </p>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 14 }}>
              {preview.sessions.map((session) => (
                <details key={session.id} open style={panel}>
                  <summary style={{ cursor: "pointer", listStyle: "none", padding: "12px 14px", display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                    <span>
                      <strong style={{ display: "block", color: "#f0f0e8", fontSize: ".84rem", textTransform: "uppercase" }}>{session.label} — {DAY_LABEL[session.preferredDay]}</strong>
                      <span style={{ display: "block", marginTop: 3, color: "#8a8a7a", fontSize: ".72rem" }}>{formatMealDays(session.mealDays)}</span>
                    </span>
                    <span style={{ color: "#a8e0c0", fontSize: ".7rem" }}>{session.components.length} préparations</span>
                  </summary>
                  <div style={{ borderTop: "1px solid rgba(212,201,168,.10)", padding: "4px 14px 14px" }}>
                    {session.components.map((component) => (
                      <div key={component.componentId} style={{ padding: "11px 0", borderBottom: "1px solid rgba(212,201,168,.08)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
                          <strong style={{ color: "#e7e4d8", fontSize: ".82rem" }}>{component.label} <span style={{ color: "#77776e", fontSize: ".68rem", fontWeight: 500 }}>· {component.usages.length} utilisation{component.usages.length > 1 ? "s" : ""}</span></strong>
                          <span style={{ color: "#d4c9a8", fontSize: ".78rem", fontWeight: 700 }}>{formatQuantity(component.quantity, component.unit)}</span>
                        </div>
                        <p style={{ margin: "7px 0 4px", color: "#77776e", fontSize: ".67rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em" }}>Cela sert pour</p>
                        <ul style={{ margin: 0, paddingLeft: 17, color: "#aaa99c", fontSize: ".74rem", lineHeight: 1.55 }}>
                          {component.usages.map((usage) => <li key={`${component.componentId}-${usage.mealId}`}>{usage.mealTitle}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                </details>
              ))}
            </div>

            <details style={{ ...panel, marginTop: 10 }}>
              <summary style={{ cursor: "pointer", padding: "12px 14px", color: "#e7e4d8", fontSize: ".82rem", fontWeight: 700 }}>Aperçu de la liste de courses</summary>
              <div style={{ borderTop: "1px solid rgba(212,201,168,.10)", padding: "12px 14px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
                {shoppingGroups.map(([category, items]) => (
                  <div key={category}>
                    <p style={{ margin: "0 0 7px", color: "#a8e0c0", fontSize: ".7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em" }}>{CATEGORY_LABELS[category] || CATEGORY_LABELS.other}</p>
                    <ul style={{ margin: 0, padding: 0, listStyle: "none", color: "#aaa99c", fontSize: ".74rem", lineHeight: 1.65 }}>
                      {items.map((item) => <li key={`${item.foodKey}-${item.unit}`}>{item.label} — {formatQuantity(item.quantity, item.unit)}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </details>
          </>
        )}

        {message && (
          <p role={message.kind === "error" ? "alert" : "status"} style={{ margin: "12px 0 0", color: message.kind === "error" ? "#f5c6c6" : "#a8e0c0", fontSize: ".76rem" }}>
            {message.text}
          </p>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          <button type="button" onClick={() => { setDraft(baseline || initial.override); setMessage(null); }} disabled={readOnly || !dirty || saving} style={{ ...button, opacity: readOnly || !dirty ? .45 : 1 }}>Annuler</button>
          <button type="button" onClick={saveDraft} disabled={readOnly || !dirty || saving || !preview.ready} style={{ ...button, color: "#d4c9a8", opacity: readOnly || !dirty || !preview.ready ? .45 : 1 }}>{saving ? "Enregistrement…" : "Enregistrer le brouillon"}</button>
          <button type="button" onClick={validate} disabled={readOnly || dirty || saving || !preview.ready || validated} style={{ ...button, borderColor: "rgba(138,191,154,.38)", background: "rgba(138,191,154,.12)", color: "#a8e0c0", opacity: readOnly || dirty || !preview.ready || validated ? .45 : 1 }}>
            {validated ? "Meal Prep validé" : "Valider le Meal Prep"}
          </button>
        </div>
      </div>
    </section>
  );
}
